import { FC, useState } from 'react';
import CustomModal from 'components/custom-modal/CustomModal';
import { FSMSubscriptionIcon, FormCloseIcon, GreenFilledTickIcon, RedCrossErrorIcon } from 'assets/icons';
import Button, { ButtonType } from 'components/custom-button/CustomButton';
import { VoidFn } from 'types/baseTypes';
import { FSM_PLANS } from '../constants';
import { FSMPlan } from 'containers/settings/constants';

// Table data
const features = [
  { label: 'Create unlimited jobs', free: true, plus: true },
  { label: 'Link estimates and invoices', free: true, plus: true },
  { label: 'Track commissions (pay per job)', free: 'Record only', plus: 'Record and see totals' },
  { label: 'Track job expenses', free: 'Record only', plus: 'Record and see profitability' },
  { label: 'Track job referrals (External company)', free: 'Record only', plus: 'Record and track payments' },
  { label: 'Assign jobs by location (Service areas)', free: false, plus: true },
  { label: 'Dispatch from map', free: false, plus: true },
  { label: 'See reports (Total, Trends)', free: false, plus: true },
  { label: 'Manage pay runs', free: false, plus: true },
  { label: 'Technician pay visibility', free: false, plus: true }
];

const getCellContent = (value: boolean | string) => {
  if (value === true) return <GreenFilledTickIcon className="inline h-4 w-4 align-middle sm:h-[18px] sm:w-[18px]" />;
  if (value === false) return <RedCrossErrorIcon className="inline h-4 w-4 align-middle sm:h-[18px] sm:w-[18px]" />;
  return (
    <span className="inline-flex items-center gap-1 text-sm font-semibold text-tertiaryText sm:gap-2 sm:text-17px">
      <GreenFilledTickIcon className="h-4 w-4 shrink-0 sm:h-[18px] sm:w-[18px]" />
      <span className="break-words">{value}</span>
    </span>
  );
};

type FSMPlanComparisonModalProps = {
  handleClose: VoidFn;
  onChoosePlan: (plan: string) => void;
  isLoading: boolean;
  currentFSMPlan: FSMPlan;
};

const FSMPlanComparisonModal: FC<FSMPlanComparisonModalProps> = ({
  handleClose,
  onChoosePlan,
  isLoading,
  currentFSMPlan
}) => {
  const [selectedPlan, setSelectedPlan] = useState<FSM_PLANS>(
    currentFSMPlan === FSMPlan.NO_PLAN ? FSM_PLANS.FIELD_SERVE : FSM_PLANS.FIELD_SERVE_PLUS
  );

  const handleSelectPlan = (plan: FSM_PLANS) => {
    setSelectedPlan(plan);
  };

  const handleChoosePlan = () => {
    onChoosePlan(selectedPlan);
  };

  return (
    <CustomModal
      open={true}
      closeModal={handleClose}
      width="w-[95%] sm:w-[90%] md:w-[80%] lg:w-[1012px] lg:max-w-5xl"
      height="h-[90vh] sm:h-[85vh] md:h-[823px] md:max-h-[90vh]"
      className="overflow-hidden">
      <div className="relative flex h-full w-full flex-col rounded-xl bg-white">
        <FormCloseIcon
          onClick={handleClose}
          className="absolute right-3 top-3 z-10 cursor-pointer sm:right-6 sm:top-6"
        />
        {/* Header */}
        <div className="mb-4 flex shrink-0 flex-col items-center px-3 pt-3 sm:mb-6 sm:px-6 sm:pt-6">
          <div
            className="mx-auto mb-2 flex h-12 w-12 items-center justify-center 
            rounded-full bg-secondaryBtn sm:mb-3 sm:h-16 sm:w-16">
            <FSMSubscriptionIcon />
          </div>
          <h2 className="mb-1 text-center text-xl font-semibold text-heading sm:mb-2 sm:text-2xl">Field Serve Plans</h2>
          <p className="px-2 text-center text-sm font-semibold text-accentText sm:text-17px">
            Choose the Field Service Plan for your team
          </p>
        </div>
        {/* Table */}
        <div
          className="max-h-[calc(100vh-300px)] min-h-0 flex-1 overflow-auto border-b border-borderGray 
        px-3 sm:max-h-[500px] sm:px-6">
          <div
            className="grid grid-cols-[minmax(120px,auto)_1fr_1fr] gap-x-2
          bg-white sm:grid-cols-[auto_1fr_1fr] sm:gap-x-4 lg:gap-x-8">
            {/* Feature column (first col) */}
            <div className="flex flex-col self-end">
              {features.map(feature => (
                <div
                  key={feature.label}
                  className="py-2 text-xs font-semibold leading-tight text-primaryText sm:py-3.5 sm:text-17px">
                  {feature.label}
                </div>
              ))}
            </div>
            {/* Field Serve Column (clickable) */}
            <button
              disabled={currentFSMPlan === FSMPlan.FREE}
              type="button"
              className={`flex flex-col items-start rounded-md border
              ${selectedPlan === FSM_PLANS.FIELD_SERVE ? 'border-secondary' : 'border-secondaryBtn'} 
              cursor-pointer`}
              tabIndex={0}
              aria-label="Select Field Serve plan"
              onClick={() => handleSelectPlan(FSM_PLANS.FIELD_SERVE)}>
              <div className="w-full bg-highlightGray p-2 text-left sm:px-4 sm:py-2">
                <div
                  className="flex flex-col gap-1 text-sm font-bold text-primaryText 
                  sm:flex-row sm:items-center sm:justify-between sm:text-[17px]">
                  <span>Field Serve</span>
                  {currentFSMPlan === FSMPlan.FREE && (
                    <span className="text-xs font-bold text-[#3E556DB2] sm:text-px13">Current plan</span>
                  )}
                </div>
                <div className="mt-1 text-xs font-semibold text-primaryText sm:text-sbase">Free</div>
                <div className="mt-1 text-xs font-medium leading-tight text-accentText sm:text-[14px]">
                  Basic job management built for small teams and owner-operators
                </div>
              </div>
              {features.map(feature => (
                <div
                  key={feature.label}
                  className="w-full border-b border-secondaryBtn p-2 text-left last:border-b-0 sm:py-3.5 sm:px-4">
                  {getCellContent(feature.free)}
                </div>
              ))}
            </button>
            {/* Field Serve Plus Column (clickable) */}
            <button
              type="button"
              className={`flex flex-col items-start rounded-md border
              ${selectedPlan === FSM_PLANS.FIELD_SERVE_PLUS ? 'border-secondary' : 'border-secondaryBtn'}
              cursor-pointer`}
              tabIndex={0}
              aria-label="Select Field Serve Plus plan"
              onClick={() => handleSelectPlan(FSM_PLANS.FIELD_SERVE_PLUS)}>
              <div className="w-full bg-highlightGray p-2 text-left sm:px-4 sm:py-2">
                <div className="text-sm font-bold text-primaryText sm:text-[17px]">Field Serve Plus</div>
                <div className="mt-1 text-xs font-semibold leading-tight text-primaryText sm:text-sbase">
                  Free for 60 days, then $99.99/month
                </div>
                <div className="mt-1 text-xs font-medium leading-tight text-accentText sm:text-[14px]">
                  Advanced dispatching and accounting for growing teams and businesses.
                </div>
              </div>
              {features.map(feature => (
                <div
                  key={feature.label}
                  className="w-full border-b border-secondaryBtn p-2 text-left last:border-b-0 sm:py-3.5 sm:px-4">
                  {getCellContent(feature.plus)}
                </div>
              ))}
            </button>
          </div>
        </div>
        {/* CTA */}
        <div className="mt-4 flex shrink-0 flex-col items-center px-3 pb-3 sm:mt-8 sm:px-6 sm:pb-4">
          <Button
            isLoading={isLoading}
            onClick={handleChoosePlan}
            id="fsm-plan-cta"
            type={ButtonType.PRIMARY}
            className="w-full text-sm sm:text-base"
            aria-label={
              selectedPlan === FSM_PLANS.FIELD_SERVE_PLUS
                ? 'Start free trial for 60 days, then $99.99 / month'
                : 'Enable Field Serve'
            }>
            {selectedPlan === FSM_PLANS.FIELD_SERVE_PLUS
              ? 'Start free trial for 60 days, then $99.99 / month'
              : 'Enable Field Serve'}
          </Button>
          <div className="mt-2 text-center text-xs text-gray-500 sm:mt-3 sm:text-sm">
            Need help?{' '}
            <a href="#" className="text-blue-600 underline hover:text-blue-800" tabIndex={0} aria-label="Chat with us">
              Chat with us
            </a>
          </div>
        </div>
      </div>
    </CustomModal>
  );
};

export default FSMPlanComparisonModal;
